import Spinner from "./Spinners";

/**
 * CustomButton — the legacy app-wide button. Originally rendered with
 * inline Tailwind for the pre-redesign chrome (rounded teal pills,
 * border-only outlines, no token reference). Now a thin wrapper over the
 * `.d-btn .d-btn-*` family defined in `dashboard-tokens.css`, so every
 * call site picks up the new Variant 7 chrome automatically — no PRs
 * touching the 35 importers needed.
 *
 * The legacy variant/size vocabulary (`default | secondary | link | ghost
 * | outline | brand`, `default | sm | lg | icon`) is preserved as the
 * public API. Internally each value maps to a `.d-btn-*` class:
 *
 *   default  → d-btn-primary    (dark CTA — Save, Submit)
 *   brand    → d-btn-accent     (accented CTA — Connect, Continue)
 *   secondary→ d-btn-secondary  (white + border — Cancel, secondary action)
 *   outline  → d-btn-secondary  (visually identical — outline is just a
 *                                pre-redesign alias for secondary)
 *   ghost    → d-btn-ghost      (transparent — overflow menu, dismiss)
 *   link     → (no .d-btn)      (renders as a plain underline-like text
 *                                link; .d-btn chrome would fight the look)
 *
 *   sm       → d-btn-sm
 *   default  → (no extra class — `.d-btn` baseline)
 *   lg       → d-btn-lg
 *   icon     → (no extra class — relies on `className` override to size
 *              the square 36×36 hit area, same as before the refactor)
 *
 * The disabled state is handled by the `.d-btn[disabled]` selector in
 * the token stylesheet (opacity 0.4 + not-allowed cursor). Loading reuses
 * the existing Spinner — same UX as before.
 *
 * `className` is appended last so per-call-site overrides (e.g.
 * `!min-w-[120px]`, `!h-7`) keep working with `!` important utilities.
 */

type LegacyVariant =
  | "default"
  | "secondary"
  | "link"
  | "ghost"
  | "outline"
  | "brand";
type LegacySize = "default" | "sm" | "lg" | "icon";

const VARIANT_TO_DBTN: Record<LegacyVariant, string> = {
  default: "d-btn-primary",
  brand: "d-btn-accent",
  secondary: "d-btn-secondary",
  outline: "d-btn-secondary",
  ghost: "d-btn-ghost",
  link: "", // handled separately — see LINK_CLASS below
};

const SIZE_TO_DBTN: Record<LegacySize, string> = {
  default: "",
  sm: "d-btn-sm",
  lg: "d-btn-lg",
  icon: "", // padding/dimensions come from caller `className`
};

// Link variant doesn't fit the .d-btn box (border/padding/background
// chrome). Keep it as a plain text-link styled with our ink token.
// Disabled link gets a muted ghost look; active link is accent-coloured
// like the rest of the app's hyperlink language.
const LINK_CLASS_BASE =
  "inline-flex items-center gap-1.5 bg-transparent p-0 text-sm font-medium leading-5 transition-colors duration-150";
const LINK_CLASS_ACTIVE =
  "text-[var(--d-accent)] hover:text-[var(--d-accent-deep)] cursor-pointer";
const LINK_CLASS_DISABLED =
  "text-[var(--d-ink-muted)] opacity-50 cursor-not-allowed";

export default function CustomButton({
  children,
  onClick,
  className,
  disabled,
  type = "button",
  loading,
  variant = "default",
  size = "lg",
  ...props
}: {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
  loading?: boolean;
  variant?: LegacyVariant;
  size?: LegacySize;
}) {
  const isDisabled = disabled || loading;

  // Link branch — bypass .d-btn chrome entirely.
  if (variant === "link") {
    const linkState = isDisabled ? LINK_CLASS_DISABLED : LINK_CLASS_ACTIVE;
    return (
      <button
        className={`${LINK_CLASS_BASE} ${linkState} ${className ?? ""}`}
        onClick={onClick}
        disabled={isDisabled}
        type={type}
        {...props}
      >
        {loading ? <Spinner /> : children}
      </button>
    );
  }

  const variantClass = VARIANT_TO_DBTN[variant];
  const sizeClass = SIZE_TO_DBTN[size];

  return (
    <button
      className={`d-btn ${variantClass} ${sizeClass} ${className ?? ""}`}
      onClick={onClick}
      disabled={isDisabled}
      type={type}
      {...props}
    >
      {loading ? <Spinner /> : children}
    </button>
  );
}
